import React, { useContext, useState } from 'react';
import { Box, Typography, Button, IconButton } from '@mui/material';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import CloseIcon from '@mui/icons-material/Close';
import RefreshIcon from '@mui/icons-material/Refresh';
import { CanvasContext } from './WorkflowCanvas';

const MESSAGES = {
  edit_lock: (lockedBy) => `This workflow is being edited by ${lockedBy}`,
  session_expired: () => 'Your editing session has expired.',
  trash_readonly: () => 'Viewing deleted workflow (read-only)',
  trash_restore: () => 'This workflow is in Trash. Restore it to make changes.',
};

/**
 * Compact top banner shown when the canvas is locked by another editing session.
 * Reads structured `editLockInfo` from context — no string matching.
 *
 * When the session has expired, exposes inline Reload (re-acquire lock without
 * refreshing the page) and Dismiss actions.
 */
export default function EditLockBanner() {
  const { editLockInfo, editLockActions } = useContext(CanvasContext);
  const [reloading, setReloading] = useState(false);

  if (!editLockInfo) return null;

  const message = MESSAGES[editLockInfo.type]?.(editLockInfo.lockedBy) || 'This workflow is locked.';
  const isSessionExpired = editLockInfo.type === 'session_expired';

  const handleReload = async () => {
    if (reloading) return;
    setReloading(true);
    try {
      await editLockActions?.tryAcquire?.();
    } finally {
      setReloading(false);
    }
  };

  const handleDismiss = () => {
    editLockActions?.dismiss?.();
  };

  return (
    <Box className="edit-lock-banner">
      <LockOutlinedIcon sx={{ fontSize: 14 }} />
      <Typography component="span" className="edit-lock-banner-text">
        {message}
      </Typography>
      {isSessionExpired && (
        <>
          <Button
            size="small"
            variant="outlined"
            startIcon={<RefreshIcon sx={{ fontSize: 14 }} />}
            onClick={handleReload}
            disabled={reloading}
            className="edit-lock-banner-button"
          >
            {reloading ? 'Reloading…' : 'Reload'}
          </Button>
          <IconButton
            size="small"
            onClick={handleDismiss}
            aria-label="Dismiss"
            className="edit-lock-banner-close"
          >
            <CloseIcon sx={{ fontSize: 14 }} />
          </IconButton>
        </>
      )}
    </Box>
  );
}
